import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { findIcon } from '../../../common/constants';
import {
    IOption,
    IOrderDetails,
    IPairAidPart,
    IProductDetail,
    ISelectedOptionIds,
    sides,
} from '../../../../models/product-detail';
import SingleOptionType from './single';
import OptionsSelectDropDown from './options-select-drop-down';
import { useIntl } from 'react-intl';

interface IDualSingleProps {
    orderDetails: IOrderDetails;
    optionType: IOption;
    optionObject?: IOption;
    selectedIdsState: ISelectedOptionIds;
    side: string;
    updateCrosPairAid: (pairAid?: IPairAidPart) => void;
    updateOptionsSelection: (right: number[], left: number[]) => void;
    productDetails: IProductDetail;
}

export default function DualSingle({
    orderDetails,
    optionType,
    optionObject,
    side,
    productDetails,
    selectedIdsState,
    updateCrosPairAid,
    updateOptionsSelection,
}: IDualSingleProps) {
    const intl = useIntl();

    const showDualSingleAsDisplay = useMemo(() => {
        return (
            (orderDetails.side === sides.right ||
                optionType.items.countWhere(
                    (opt: IOption) => opt.isAvailableLeft && !opt.hidden
                ) === 1) &&
            (orderDetails.side === sides.left ||
                optionType.items.countWhere(
                    (opt) => opt.isAvailableRight && !opt.hidden
                ) === 1)
        );
    }, [optionType]);

    const staticName = useMemo(() => {
        const availableOptions = optionType.items.filter(
            (opt) =>
                (opt.isAvailableLeft || opt.isAvailableRight) && !opt.hidden
        );
        if (availableOptions.length === 1) return availableOptions[0].name;
        const leftAvailableOptions = availableOptions.filter(
            (opt) => opt.isAvailableLeft && !opt.hidden
        );
        const rightAvailableOptions = availableOptions.filter(
            (opt) => opt.isAvailableRight && !opt.hidden
        );
        const result: IOption[] = [];
        if (rightAvailableOptions.length === 1)
            result.push(rightAvailableOptions[0]);
        if (leftAvailableOptions.length === 1)
            result.push(leftAvailableOptions[0]);
        return result.map((opt) => opt.name).join(', ');
    }, [optionType]);

    return showDualSingleAsDisplay ? (
        <h4>
            <FontAwesomeIcon
                icon={findIcon('fad', 'check')}
                className="font-green"
            />{' '}
            {staticName}
        </h4>
    ) : (
        <>
            <SingleOptionType
                options={optionType.items}
                orderDetails={orderDetails}
                onChange={updateOptionsSelection}
                optionName={optionType.english}
                selectedSide={
                    side === 'B'
                        ? sides.binaural
                        : side === 'R'
                        ? sides.right
                        : sides.left
                }
                multiSelect={false}
                selectedIds={selectedIdsState}
            />
            {productDetails.crosHearingAidTechnologies &&
                (selectedIdsState!.rightSelectedIds.includes(511) ||
                    selectedIdsState!.leftSelectedIds.includes(511)) &&
                optionType.id === 64 && (
                    <OptionsSelectDropDown
                        name={'hearingAidTechnologySelection'}
                        label={intl.formatMessage({
                            id: 'Hearing_Aid_Technology',
                            defaultMessage: 'Hearing Aid Technology',
                        })}
                        options={Array.from(
                            new Set(
                                productDetails.crosHearingAidTechnologies.map(
                                    (x: any) => {
                                        return {
                                            ...optionObject!,
                                            id: x.designId,
                                            name: x.displayName,
                                        };
                                    }
                                )
                            )
                        )}
                        hasDefaultOption={true}
                        defaultOption={
                            intl.formatMessage({
                                id: 'Select',
                                defaultMessage: 'Select',
                            }) + '...'
                        }
                        labelClassName={'col-sm-12'}
                        value={
                            orderDetails!.pairAid
                                ? orderDetails.pairAid.designId
                                : ''
                        }
                        onChange={(e) =>
                            updateCrosPairAid(
                                productDetails.crosHearingAidTechnologies.find(
                                    (x) =>
                                        x.designId === parseInt(e.target.value)
                                )!
                            )
                        }
                        required={true}
                        customLabel={true}
                        inlineStyle={{
                            borderColor:
                                selectedIdsState!.rightSelectedIds.includes(511)
                                    ? 'red'
                                    : 'blue',
                        }}
                    />
                )}
        </>
    );
}
